import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Gamepad2 } from 'lucide-react';
import ProductCarousel from '@/components/ProductCarousel';
import AnnouncementCarousel from '@/components/AnnouncementCarousel';
import SubscriptionCarousel from '@/components/SubscriptionCarousel';
import { productService, GameListFilters } from './productService';
import { GameCard } from './types';

const PLATFORM_QUERY = 'platforms=PS4&platforms=PS5';
const SECTION_SIZE = 12;

type Section = {
  key: string;
  title: string;
  description: string;
  href: string;
  filters: Partial<GameListFilters>;
};

const SECTIONS: Section[] = [
  { key: 'new', title: 'Новинки', description: 'Свежие релизы для PlayStation', href: `/catalog?${PLATFORM_QUERY}&brandNew=true`, filters: { brandNew: true } },
  { key: 'popular', title: 'Лидеры продаж', description: 'Игры, которые выбирают чаще всего', href: `/catalog?${PLATFORM_QUERY}&salesLeader=true`, filters: { salesLeader: true } },
  { key: 'preorders', title: 'Предзаказы', description: 'Предзаказы и ближайшие релизы', href: `/catalog?${PLATFORM_QUERY}&preOrder=true`, filters: { preOrder: true } },
  { key: 'lowest-price', title: 'Дешевле не было', description: 'Подборка игр по самым выгодным ценам', href: `/catalog?${PLATFORM_QUERY}&lowestPrice=true`, filters: { lowestPrice: true } },
  { key: 'top-metacritic', title: 'Лучшие по Metacritic', description: 'Игры с высокими оценками критиков', href: `/catalog?${PLATFORM_QUERY}&topMetacritic=true`, filters: { topMetacritic: true } },
  { key: 'action', title: 'Экшен', description: 'Динамичные приключения и сражения', href: `/catalog?${PLATFORM_QUERY}&genres=Action`, filters: { genres: ['Action'] } },
  { key: 'rpg', title: 'RPG', description: 'Большие миры и собственные истории', href: `/catalog?${PLATFORM_QUERY}&genres=RPG`, filters: { genres: ['RPG'] } },
  { key: 'adventure', title: 'Приключения', description: 'Исследования, загадки и сильные сюжеты', href: `/catalog?${PLATFORM_QUERY}&genres=Adventure`, filters: { genres: ['Adventure'] } },
  { key: 'racing', title: 'Гонки', description: 'Скорость, трассы и автомобили', href: `/catalog?${PLATFORM_QUERY}&genres=Racing`, filters: { genres: ['Racing'] } },
  { key: 'sports', title: 'Спорт', description: 'Футбол, хоккей и другие соревнования', href: `/catalog?${PLATFORM_QUERY}&genres=Sports`, filters: { genres: ['Sports'] } },
];

const PlayStationStorePage = () => {
  const [products, setProducts] = useState<Record<string, GameCard[]>>({});
  const [subscriptions, setSubscriptions] = useState<GameCard[]>([]);
  const [subscriptionsFailed, setSubscriptionsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const [sectionsResult, subscriptionsResult] = await Promise.allSettled([
        Promise.allSettled(SECTIONS.map(section =>
          productService.fetchProducts({
            ...section.filters,
            platforms: ['PS4', 'PS5'],
            pageNumber: 1,
            pageSize: SECTION_SIZE,
          }),
        )),
        productService.fetchHome(),
      ]);
      if (cancelled) return;
      const results = sectionsResult.status === 'fulfilled' ? sectionsResult.value : [];
      if (subscriptionsResult.status === 'fulfilled') {
        setSubscriptions(subscriptionsResult.value.serviceGroups.find(group => group.category === 'Subscription')?.services ?? []);
      }
      setSubscriptionsFailed(subscriptionsResult.status === 'rejected');
      const next: Record<string, GameCard[]> = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') next[SECTIONS[index].key] = result.value.products as GameCard[];
      });
      setProducts(next);
      setFailed(results.every(result => result.status === 'rejected'));
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-background px-4 py-6 sm:py-8">
      <div className="mx-auto max-w-[90rem]">
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-foreground/45 transition hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> На главную
        </Link>

        <AnnouncementCarousel placement="playstation" />

        {!loading && subscriptions.length > 0 && (
          <section className="relative mb-12 overflow-hidden rounded-[28px] border border-red-500/15 bg-[linear-gradient(135deg,rgba(239,68,68,0.10),rgba(127,29,29,0.04)_48%,rgba(255,255,255,0.02))] px-4 py-7 shadow-sm sm:px-7 sm:py-9 lg:px-10 lg:py-11">
            <div className="pointer-events-none absolute -right-24 -top-32 h-80 w-80 rounded-full bg-red-500/10 blur-3xl" />
            <div className="relative">
              <SubscriptionCarousel products={subscriptions} />
            </div>
          </section>
        )}

        {subscriptionsFailed && <div className="mb-12 rounded-[28px] border border-red-500/20 bg-red-500/10 p-8 text-center text-foreground/65">Не удалось загрузить подписки PS Plus. Попробуйте обновить страницу.</div>}

        {loading && (
          <div className="space-y-12">
            {[1, 2, 3].map(item => <div key={item}><div className="mb-5 h-8 w-44 animate-pulse rounded bg-foreground/5" /><div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">{[1, 2, 3, 4, 5, 6].map(card => <div key={card} className="aspect-[4/5] animate-pulse rounded-[24px] bg-foreground/5" />)}</div></div>)}
          </div>
        )}

        {failed && <div className="rounded-[28px] border border-red-500/20 bg-red-500/10 p-8 text-center text-foreground/65">Не удалось загрузить подборки. Попробуйте обновить страницу.</div>}

        {!loading && SECTIONS.map(section => {
          const items = products[section.key] ?? [];
          if (items.length === 0) return null;
          return (
            <section key={section.key} className="mb-12">
              <div className="mb-5">
                <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">{section.title}</h2>
                <p className="mt-1 text-sm text-foreground/45">{section.description}</p>
              </div>
              <ProductCarousel products={items} viewAllHref={section.href} />
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default PlayStationStorePage;
